import React, { useState, useEffect } from 'react';
import { Settings, Save, Trash2, Plus, Calendar, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import ValetManagement from './ValetManagement';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Notification Component
const Notification = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900';
  const icon = type === 'success' ? '✓' : '⚠';

  return (
    <div className={`rounded-lg border p-4 flex gap-3 animate-in fade-in ${bgColor}`}>
      <span className="text-xl flex-shrink-0">{icon}</span>
      <p className="text-sm font-medium">{message}</p>
    </div>
  );
};

// VALET ROTA EDITOR
function ValetRotaEditor() {
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [editingRota, setEditingRota] = useState(null);

  useEffect(() => {
    fetchRotas();
  }, []);

  const fetchRotas = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/valet-rota`);
      if (response.ok) {
        const data = await response.json();
        setRotas(data.data);
      }
    } catch (error) {
      console.error('Error fetching rotas:', error);
      setNotification({ message: 'Failed to load rotas', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCapacityChange = (valetId, day, capacity) => {
    setEditingRota(prev => ({
      ...prev,
      [`${day}_capacity`]: Math.max(1, Math.min(5, parseInt(capacity) || 0))
    }));
  };

  const handleAvailabilityToggle = (valetId, day) => {
    setEditingRota(prev => ({
      ...prev,
      [`${day}_available`]: !prev[`${day}_available`]
    }));
  };

  const handleSaveRota = async () => {
    if (!editingRota) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/valet-rota`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editingRota,
          updated_by: 'admin'
        })
      });

      if (response.ok) {
        setNotification({ message: 'Rota updated successfully!', type: 'success' });
        setEditingRota(null);
        fetchRotas();
      }
    } catch (error) {
      setNotification({ message: 'Failed to save rota', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading rotas...</div>;

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          Configure which days each valet works and their daily capacity (max bookings per day).
        </p>
      </div>

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Valet</th>
              {dayLabels.map((day, idx) => (
                <th key={day} className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                  {day}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rotas.map((rota) => (
              <tr key={rota.valet_id} className="border-b border-slate-200 hover:bg-slate-50 transition">
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{rota.name}</td>
                {days.map((day) => {
                  const isEditing = editingRota?.valet_id === rota.valet_id;
                  const available = isEditing ? editingRota[`${day}_available`] : rota[`${day}_available`];
                  const capacity = isEditing ? editingRota[`${day}_capacity`] : rota[`${day}_capacity`];

                  return (
                    <td key={day} className="px-4 py-3 text-center">
                      {isEditing ? (
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            checked={available}
                            onChange={() => handleAvailabilityToggle(rota.valet_id, day)}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={capacity}
                            onChange={(e) => handleCapacityChange(rota.valet_id, day, e.target.value)}
                            className="w-12 px-2 py-1 text-center text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      ) : (
                        <div className="text-sm">
                          {available ? (
                            <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded">
                              ✓ {capacity}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-2 py-1 rounded">
                              ✗
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-4 py-3 text-center">
                  {editingRota?.valet_id === rota.valet_id ? (
                    <button
                      onClick={handleSaveRota}
                      disabled={saving}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      Save
                    </button>
                  ) : (
                    <button
                      onClick={() => setEditingRota(rota)}
                      className="px-3 py-1 bg-slate-200 text-slate-700 text-sm rounded hover:bg-slate-300 transition"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// HOLIDAY MANAGER
function HolidayManager() {
  const [rotas, setRotas] = useState([]);
  const [selectedValet, setSelectedValet] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: '', reason: '' });
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchRotas();
  }, []);

  useEffect(() => {
    if (selectedValet) {
      fetchHolidays(selectedValet);
    }
  }, [selectedValet]);

  const fetchRotas = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/valet-rota`);
      if (response.ok) {
        const data = await response.json();
        setRotas(data.data);
      }
    } catch (error) {
      console.error('Error fetching rotas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async (valetId) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/valet-holidays/${valetId}`);
      if (response.ok) {
        const data = await response.json();
        setHolidays(data.data);
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const handleAddHoliday = async () => {
    if (!selectedValet || !newHoliday.date) {
      setNotification({ message: 'Please select valet and date', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/settings/valet-holidays`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          valet_id: selectedValet,
          holiday_date: newHoliday.date,
          reason: newHoliday.reason
        })
      });

      if (response.ok) {
        setNotification({ message: 'Holiday added successfully!', type: 'success' });
        setNewHoliday({ date: '', reason: '' });
        fetchHolidays(selectedValet);
      }
    } catch (error) {
      setNotification({ message: 'Failed to add holiday', type: 'error' });
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/valet-holidays/${holidayId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setNotification({ message: 'Holiday deleted successfully!', type: 'success' });
        fetchHolidays(selectedValet);
      }
    } catch (error) {
      setNotification({ message: 'Failed to delete holiday', type: 'error' });
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          Mark specific dates when valets are unavailable (holidays, sick leave, etc.).
        </p>
      </div>

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Holiday</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Valet</label>
            <select
              value={selectedValet}
              onChange={(e) => setSelectedValet(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose a valet...</option>
              {rotas.map((rota) => (
                <option key={rota.valet_id} value={rota.valet_id}>
                  {rota.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
            <input
              type="date"
              value={newHoliday.date}
              onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Reason</label>
            <input
              type="text"
              placeholder="e.g., Christmas, Sick leave"
              value={newHoliday.reason}
              onChange={(e) => setNewHoliday({ ...newHoliday, reason: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <button
          onClick={handleAddHoliday}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Holiday
        </button>
      </div>

      {selectedValet && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Holidays for {rotas.find(r => r.valet_id === selectedValet)?.name}</h3>

          {holidays.length === 0 ? (
            <p className="text-slate-500 text-sm">No holidays scheduled</p>
          ) : (
            <div className="space-y-2">
              {holidays.map((holiday) => (
                <div key={holiday.holiday_id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {new Date(holiday.holiday_date).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-slate-500">{holiday.reason}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteHoliday(holiday.holiday_id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// SLOT SETTINGS EDITOR
function SlotSettingsEditor() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [editedSettings, setEditedSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/slot-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data.data);
        setEditedSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setNotification({ message: 'Failed to load settings', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/slot-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editedSettings,
          updated_by: 'admin'
        })
      });

      if (response.ok) {
        setNotification({ message: 'Settings saved successfully!', type: 'success' });
        fetchSettings();
      }
    } catch (error) {
      setNotification({ message: 'Failed to save settings', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading settings...</div>;
  if (!editedSettings) return <div className="text-center py-8">Settings not found</div>;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          Configure global slot settings that apply to all valets and bookings.
        </p>
      </div>

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Slot Configuration */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" /> Slot Configuration
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Slot Duration (minutes)</label>
              <input
                type="number"
                value={editedSettings.slot_duration_minutes}
                onChange={(e) => setEditedSettings({ ...editedSettings, slot_duration_minutes: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Work Start Time</label>
              <input
                type="time"
                value={editedSettings.slot_start_time?.substring(0, 5)}
                onChange={(e) => setEditedSettings({ ...editedSettings, slot_start_time: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Work End Time</label>
              <input
                type="time"
                value={editedSettings.slot_end_time?.substring(0, 5)}
                onChange={(e) => setEditedSettings({ ...editedSettings, slot_end_time: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Break Start Time</label>
              <input
                type="time"
                value={editedSettings.break_start_time?.substring(0, 5)}
                onChange={(e) => setEditedSettings({ ...editedSettings, break_start_time: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Break End Time</label>
              <input
                type="time"
                value={editedSettings.break_end_time?.substring(0, 5)}
                onChange={(e) => setEditedSettings({ ...editedSettings, break_end_time: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Booking Rules */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> Booking Rules
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Lead Time (hours)</label>
              <input
                type="number"
                value={editedSettings.lead_time_hours}
                onChange={(e) => setEditedSettings({ ...editedSettings, lead_time_hours: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum hours in advance before booking</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Max Advance Days</label>
              <input
                type="number"
                value={editedSettings.max_advance_days}
                onChange={(e) => setEditedSettings({ ...editedSettings, max_advance_days: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-500 mt-1">Maximum days in advance for booking</p>
            </div>
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Company Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
            <input
              type="text"
              value={editedSettings.company_name || ''}
              onChange={(e) => setEditedSettings({ ...editedSettings, company_name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
            <input
              type="tel"
              value={editedSettings.company_phone || ''}
              onChange={(e) => setEditedSettings({ ...editedSettings, company_phone: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
            <input
              type="email"
              value={editedSettings.company_email || ''}
              onChange={(e) => setEditedSettings({ ...editedSettings, company_email: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex gap-4 justify-end">
        <button
          onClick={() => setEditedSettings(settings)}
          className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition font-medium"
        >
          Reset
        </button>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          Save Settings
        </button>
      </div>
    </div>
  );
}

// MAIN SETTINGS PAGE
export default function SettingsPage({ onNavigateBack }) {
  const [activeTab, setActiveTab] = useState('rota');

  const tabs = [
    { id: 'manage', label: 'Manage Valets', icon: '👤' },
    { id: 'rota', label: 'Valet Rota', icon: '👥' },
    { id: 'holidays', label: 'Holidays', icon: '🏖️' },
    { id: 'settings', label: 'Slot Settings', icon: '⚙️' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                  Settings
                </h1>
                <p className="text-sm font-medium text-slate-500 mt-1">Manage valets, rotas, and booking configuration</p>
              </div>
            </div>
            {onNavigateBack && (
              <button 
                onClick={onNavigateBack}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition text-sm font-semibold"
              >
                Back
              </button>
            )}
          </div>
        </div>
      </header>

      {/* TAB NAVIGATION */}
      <div className="max-w-6xl mx-auto px-8 py-8">
        <div className="flex gap-3 mb-10">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB CONTENT */}
        <div className="space-y-6">
          {activeTab === 'manage' && <ValetManagement />}
          {activeTab === 'rota' && <ValetRotaEditor />}
          {activeTab === 'holidays' && <HolidayManager />}
          {activeTab === 'settings' && <SlotSettingsEditor />}
        </div>
      </div>
    </div>
  );
}
